/**
 * React context plumbing for the per-route edit store.
 *
 * Pattern:
 *   - <EditStoreProvider> instantiates one ProvisionalForecastV2EditStore
 *     for the current Edit-route mount and provides it to its subtree.
 *     The store is created via useState(() => new Store()) so it survives
 *     re-renders but a fresh component mount yields a fresh store.
 *   - useEditStore() in any descendant returns the same instance.
 *   - On unmount, the provider calls store.reset() — defensive: even
 *     though the instance itself goes away, resetting clears any
 *     pending observable subscriptions cleanly.
 *
 * Why a context (and not just a hook that returns a singleton)
 * ------------------------------------------------------------
 * The store must NOT be a module-level singleton — visiting the Edit
 * page twice with different NDC sets would otherwise leak state from
 * the first visit into the second. Tying lifetime to the React tree is
 * what makes "leave the page, come back" feel clean.
 */

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { ProvisionalForecastV2EditStore } from './editStore';

const EditStoreContext = createContext<ProvisionalForecastV2EditStore | null>(null);

interface EditStoreProviderProps {
  children: ReactNode;
}

export const EditStoreProvider = ({ children }: EditStoreProviderProps) => {
  const [store] = useState(() => new ProvisionalForecastV2EditStore());

  useEffect(() => {
    return () => {
      // Belt-and-braces — the instance is GC'd on unmount, but
      // resetting first stops any in-flight render observers from
      // staring at stale data during teardown.
      store.reset();
    };
  }, [store]);

  return (
    <EditStoreContext.Provider value={store}>{children}</EditStoreContext.Provider>
  );
};

export const useEditStore = (): ProvisionalForecastV2EditStore => {
  const store = useContext(EditStoreContext);
  if (!store) {
    throw new Error(
      'useEditStore() must be called inside <EditStoreProvider>. Wrap the ' +
        'Edit-route component tree in <EditStoreProvider> before rendering.'
    );
  }
  return store;
};
